import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Search, BarChart3, Users, RefreshCw, AlertCircle } from 'lucide-react'
import { PageWrapper } from '@/components/animated/page-wrapper'
import { StaggerContainer, StaggerItem } from '@/components/animated/stagger-container'
import { InteractiveCard } from '@/components/animated/interactive-card'
import { useDashboardData } from '@/hooks/use-dashboard-data'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AskAIBernhardWidget } from '@/components/dashboard/ask-ai-bernhard-widget'

export function Dashboard() {
  const { stats, categoryBreakdown, recentActivities, isLoading, error, refreshData } = useDashboardData()

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('de-DE').format(num)
  }

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'approved': return 'bg-green-500'
      case 'created': return 'bg-blue-500'
      case 'updated': return 'bg-yellow-500'
      case 'archived': return 'bg-gray-500'
      default: return 'bg-blue-500'
    }
  }

  const getTimeAgo = (timestamp: string) => {
    const now = new Date()
    const time = new Date(timestamp)
    const diffInHours = Math.floor((now.getTime() - time.getTime()) / (1000 * 60 * 60))

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - time.getTime()) / (1000 * 60))
      return `vor ${diffInMinutes} Minuten`
    } else if (diffInHours < 24) {
      return `vor ${diffInHours} Stunden`
    } else {
      const diffInDays = Math.floor(diffInHours / 24)
      return `vor ${diffInDays} Tagen`
    }
  }

  return (
    <PageWrapper>
      <div className="space-y-6 bg-gray-50 dark:bg-background min-h-screen -m-6 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-light tracking-tight text-foreground">Dashboard</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={refreshData} disabled={isLoading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error}
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        <StaggerContainer className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StaggerItem>
            <InteractiveCard>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Gesamt FAQs</CardTitle>
                  <Search className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {isLoading ? (
                      <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                    ) : (
                      formatNumber(stats.totalFAQs)
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Alle FAQs in der Datenbank
                  </p>
                </CardContent>
              </Card>
            </InteractiveCard>
          </StaggerItem>

          <StaggerItem>
            <InteractiveCard>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Aktive FAQs</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {isLoading ? (
                      <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                    ) : (
                      formatNumber(stats.activeFAQs)
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Genehmigte und aktive FAQs
                  </p>
                </CardContent>
              </Card>
            </InteractiveCard>
          </StaggerItem>

          <StaggerItem>
            <InteractiveCard>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Zur Prüfung</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {isLoading ? (
                      <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                    ) : (
                      formatNumber(stats.pendingReview)
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    FAQs warten auf Genehmigung
                  </p>
                </CardContent>
              </Card>
            </InteractiveCard>
          </StaggerItem>

          <StaggerItem>
            <InteractiveCard>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Suchaufrufe</CardTitle>
                  <Search className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {isLoading ? (
                      <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                    ) : (
                      formatNumber(stats.totalSearchQueries)
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Gesamte FAQ-Aufrufe
                  </p>
                </CardContent>
              </Card>
            </InteractiveCard>
          </StaggerItem>
        </StaggerContainer>

        {/* KI Bernhard Testing Widget - Full Width */}
        <div className="w-full">
          <AskAIBernhardWidget />
        </div>
        </div>
      </div>
    </PageWrapper>
  )
}